import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Prompt } from 'react-router';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';

import {
  VpPluginSortingSelect,
  VpPluginLogoCheckbox,
  VpIcon,
  VpConditionalUpgradeWrapper
} from '@vp-ui';
import VpAdjustPluginVideoButton from '@vp-ui/widgets/VpPluginDetailsForm/VpAdjustPluginVideoButton';
import { StyledBox } from '@vp-ui/widgets/VpPluginDetailsForm/styles';
import VpPluginTitleSection from '@vp-ui/widgets/VpPluginDetailsForm/VpPluginTitleSection';
import VpPluginDescriptionSection from '@vp-ui/widgets/VpPluginDetailsForm/VpPluginDescriptionSection';
import VpPluginBorderSection from '@vp-ui/widgets/VpPluginDetailsForm/VpPluginBorderSection';
import VpPluginBoxShadowSection from '@vp-ui/widgets/VpPluginDetailsForm/VpPluginBoxShadowSection';
import VpPluginCardInputs from '@vp-ui/form-fields/plugin-details/VpPluginCardInputs';
import VpPluginDateSubmittedSection from '@vp-ui/widgets/VpPluginDetailsForm/VpPluginDateSubmittedSection';
import {
  getAttachmentPluginsEnabled,
  getPluginManagementEnabled,
  getPluginRemoveLogoEnabled,
  getPluginStylesEditingEnabled,
  getUser
} from '@src/redux/modules/auth';
import {
  getIsOrganizationViewer,
  getSelectedOrganization
} from '@src/redux/modules/organization';
import { isFree } from '@src/helpers/plansHelper';
import { useFetchPluginById, usePluginHelpers } from '@src/hooks';
import config from '@src/config';
import { useUpdatePluginFieldMapping } from '@src/hooks/react-query/usePluginData';
import VpFieldMappingInputs from '@vp-ui/form-fields/VpFieldMappingInputs/VpFieldMappingInputs';
import { useVpSnackbar } from '@vp-ui/hooks/useVpSnackbar';
import FormHelperText from '@mui/material/FormHelperText';

const PluginFieldMappingSection = () => {
  const { data: pluginData } = useFetchPluginById();
  const pluginId = pluginData?.id;
  const savedTitleElementId = pluginData?.titleElementId || null;
  const savedDescriptionElementId = pluginData?.descriptionElementId || null;

  const { mutate: saveMapping, isLoading } = useUpdatePluginFieldMapping();
  const { addSnackbarSuccess, addSnackbarError } = useVpSnackbar();

  const [localMapping, setLocalMapping] = useState(null);

  const titleElementId = localMapping ? localMapping.titleElementId : savedTitleElementId;
  const descriptionElementId = localMapping ? localMapping.descriptionElementId : savedDescriptionElementId;

  const handleSave = () => {
    saveMapping(
      { pluginId, titleElementId, descriptionElementId },
      {
        onSuccess: () => {
          setLocalMapping(null);
          addSnackbarSuccess({ message: 'Field mapping saved.' });
        },
        onError: (e) => addSnackbarError({ error: e })
      }
    );
  };

  return (
    <Stack spacing="8px">
      <Typography variant="formSectionTitle">Field Mapping</Typography>
      <FormHelperText>
        Campaign-specific fields are configured per video when adding them to this plugin.
        Here you can set a global mapping using the submitter name or clear an existing one.
      </FormHelperText>
      <VpFieldMappingInputs
        elements={[]}
        titleElementId={titleElementId}
        descriptionElementId={descriptionElementId}
        onChange={setLocalMapping}
      />
      <Button
        variant="contained"
        size="small"
        disabled={isLoading || !localMapping}
        onClick={handleSave}
        sx={{ alignSelf: 'flex-start' }}
      >
        Save field mapping
      </Button>
    </Stack>
  );
};

const billingPageLink = <Link to="/billing">billing page</Link>;

const VpPluginDetailsFormComponent = ({
  pluginStylesEditingEnabled,
  pluginManagementEnabled,
  pluginRemoveLogoEnabled,
  isOrgViewer,
  user,
  organization,
  includeAttachments
}) => {
  const { hasPublishChanges, isDirty } = usePluginHelpers(config?.pluginFontSizeEnabled);

  const isFreeAccount = isFree({ user, organization });

  const showFeaturesChip =
    !isOrgViewer &&
    (!pluginStylesEditingEnabled || !pluginManagementEnabled || !pluginRemoveLogoEnabled);

  const upgradePrompt = isOrgViewer ? (
    'You do not have permissions to perform this action for this organization.'
  ) : (
    <span>
      Upgrade on the {billingPageLink} to customize your plugin even more with customized, titles,
      descriptions, theme colors, and more!
    </span>
  );

  const promptMessage = `You have ${
    hasPublishChanges ? 'unpublished' : 'unsaved'
  } changes, are you sure you want to leave this page?`;

  return (
    <>
      <Prompt
        message={(location, action) => {
          const dirtyForm = hasPublishChanges || isDirty;
          const showMessage =
            dirtyForm &&
            pluginManagementEnabled &&
            !isOrgViewer &&
            location?.state?.from !== 'PLUGIN_PAGE_ADJUST_VIDEOS' &&
            action !== 'REPLACE';

          return showMessage ? promptMessage : true;
        }}
      />
      <StyledBox>
        <Stack spacing="18px">
          <Stack direction="row" spacing="15px" alignItems="center">
            <Typography variant="formSectionTitle">Details</Typography>
            {showFeaturesChip && (
              <VpConditionalUpgradeWrapper condition={showFeaturesChip} label={upgradePrompt}>
                <Chip
                  icon={<VpIcon icon="Lock" />}
                  label={isFreeAccount ? 'Paid Features' : 'Premium Features'}
                  variant="primaryButton"
                />
              </VpConditionalUpgradeWrapper>
            )}
          </Stack>
          <VpAdjustPluginVideoButton includeAttachments={includeAttachments} />
          <VpPluginSortingSelect />
          <VpPluginLogoCheckbox />
          <Divider />
          <Typography variant="formSectionTitle">Text</Typography>
          <VpPluginTitleSection />
          <VpPluginDateSubmittedSection />
          <VpPluginDescriptionSection />
          <Divider />
          <Typography variant="formSectionTitle">Cards</Typography>
          <VpPluginCardInputs />
          <VpPluginBorderSection />
          <VpPluginBoxShadowSection />
          <Divider />
          <PluginFieldMappingSection />
        </Stack>
      </StyledBox>
    </>
  );
};

VpPluginDetailsFormComponent.propTypes = {
  pluginStylesEditingEnabled: PropTypes.bool,
  pluginManagementEnabled: PropTypes.bool,
  pluginRemoveLogoEnabled: PropTypes.bool,
  isOrgViewer: PropTypes.bool,
  user: PropTypes.object,
  organization: PropTypes.object,
  includeAttachments: PropTypes.bool
};

const mapStateToProps = (state) => ({
  pluginStylesEditingEnabled: getPluginStylesEditingEnabled(state),
  pluginManagementEnabled: getPluginManagementEnabled(state),
  pluginRemoveLogoEnabled: getPluginRemoveLogoEnabled(state),
  isOrgViewer: getIsOrganizationViewer(state),
  user: getUser(state),
  organization: getSelectedOrganization(state),
  includeAttachments: getAttachmentPluginsEnabled(state)
});

export const VpPluginDetailsForm = connect(mapStateToProps)(VpPluginDetailsFormComponent);

export default connect(mapStateToProps)(VpPluginDetailsFormComponent);



// WEBPACK FOOTER //
// ./src/vp-ui/widgets/VpPluginDetailsForm/VpPluginDetailsForm.jsx